import {HandleType} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureEdgeType} from 'procedures/ProcedureDomainTypes';

const HANDLE_COMPONENT_SEPARATOR = '$';

export const enum ProcedureHandleObjectType {
  METHOD = 'METHOD',
  MATERIAL = 'MATERIAL',
}

interface ProcedureHandleData {
  procedureHandleType: ProcedureHandleObjectType;
  handleType: HandleType; // source | target
}

/**
 * Builds a deterministic edge ID from a source and target handle
 */
export function createEdgeId(sourceHandle: string, targetHandle: string): string {
  return `edge_${sourceHandle}${HANDLE_COMPONENT_SEPARATOR}${targetHandle}`;
}

export function createHandleIdForMethod(methodCid: string, handleType: HandleType): string {
  return `${handleType === 'source' ? 'SM_' : 'TM_'}${methodCid}`;
}

export function createHandleIdForMaterial(
  methodCid: string,
  materialCid: string,
  handleType: HandleType
): string {
  return `${handleType === 'source' ? 'SMO_' : 'TMI_'}${
    methodCid || 'none'
  }${HANDLE_COMPONENT_SEPARATOR}${materialCid}`;
}

export function getHandleType(handleId: string): ProcedureHandleObjectType {
  const prefix = handleId.split('_')[0];

  // Just to be clear: the prefixes you see in this file STAY in this file. They are private. Do not use them elsewhere!
  switch (prefix) {
    case 'SM':
    case 'TM':
      return ProcedureHandleObjectType.METHOD;
    case 'SMO':
    case 'TMI':
      return ProcedureHandleObjectType.MATERIAL;
    default:
      assert(false, `Invalid handle id: ${handleId}`);
  }
}

export function getHandleTypeDetails(handleId: string): ProcedureHandleData {
  const prefix = handleId.split('_')[0];
  const map: Record<string, ProcedureHandleData> = {
    SM: {procedureHandleType: ProcedureHandleObjectType.METHOD, handleType: 'source'},
    TM: {procedureHandleType: ProcedureHandleObjectType.METHOD, handleType: 'target'},
    SMO: {procedureHandleType: ProcedureHandleObjectType.MATERIAL, handleType: 'source'},
    TMI: {procedureHandleType: ProcedureHandleObjectType.MATERIAL, handleType: 'target'},
  };
  const data = map[prefix];
  assert(data != null, `Invalid handle id: ${handleId}`);
  return data;
}

export function getEdgeTypeFromHandleId(handleId: string): ProcedureEdgeType {
  return getHandleType(handleId) === ProcedureHandleObjectType.METHOD
    ? ProcedureEdgeType.METHOD_EDGE
    : ProcedureEdgeType.MATERIAL_EDGE;
}

export function getMaterialCidFromHandle(handle: string): string {
  const handleSplits = handle.split(HANDLE_COMPONENT_SEPARATOR);
  assert(handleSplits.length === 2, 'Invalid handle string provided');
  return handleSplits[1];
}
